// pages/admin/complaints/ComplaintDetails.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { lazily } from "react-lazily";
import TaleButton from "@components/TaleButton";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { complaintsService } from "@/api/complaints";
import { ComplaintStatusBadge } from "@/pages/admin/complaints/AdminComplaintsList";
import { FiArrowLeft, FiBriefcase, FiCheckCircle, FiSend, FiUser } from "react-icons/fi";
import { useTranslation } from "@/hooks/useTranslation";

const { InternalServerError } = lazily(() =>
    import("@components/Errors/InternalServerError")
);

const formatDateTime = (value) =>
    value
        ? new Date(value).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
        : "-";

const fullName = (person) =>
    [person?.firstname, person?.lastname].filter(Boolean).join(" ") || person?.email || "";

// Single place to adapt if the backend message fields differ
const normalizeMessage = (m) => ({
    id: m.id,
    text: m.body ?? "",
    sender: m.author ?? m.sender ?? null,
    fromAdmin: Boolean(m.fromAdmin ?? m.isAdmin ?? m.admin),
    createdAt: m.createdAt ?? m.sentAt,
});

const Avatar = ({ person, admin }) => (
    <div
        className={`flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center overflow-hidden ${
            admin ? "bg-gradient-to-br from-cyan-100 to-cyan-200" : "bg-gradient-to-br from-blue-100 to-blue-200"
        }`}
    >
        {person?.profilePictureUrl ? (
            <img src={person.profilePictureUrl} alt="" className="h-full w-full object-cover" />
        ) : (
            <FiUser className={`w-4 h-4 ${admin ? "text-cyan-700" : "text-blue-600"}`} />
        )}
    </div>
);

const ComplaintDetails = ({ basePath = "/admin/complaints", isAdmin = true }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const threadEndRef = useRef(null);

    const [reply, setReply] = useState("");
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [closingMessage, setClosingMessage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionError, setActionError] = useState("");

    const {
        isPending: loading,
        data: complaint,
        error,
        refetch,
    } = useQuery({
        queryKey: ["complaint", id],
        queryFn: () => complaintsService.getComplaintById(id),
        retry: false,
        refetchOnWindowFocus: false,
    });

    const messages = useMemo(
        () => (complaint?.messages || []).map(normalizeMessage),
        [complaint]
    );

    const isResolved = complaint?.status === "RESOLVED";

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [messages.length]);

    const handleSend = async () => {
        if (!reply.trim()) return;
        setIsProcessing(true);
        setActionError("");
        try {
            await complaintsService.replyToComplaint(id, reply.trim());
            setReply("");
            await refetch();
        } catch {
            setActionError(t("complaints.actionError"));
        } finally {
            setIsProcessing(false);
        }
    };

    const confirmResolve = async () => {
        setIsProcessing(true);
        setActionError("");
        try {
            await complaintsService.resolveComplaint(id, closingMessage);
            setShowResolveModal(false);
            setClosingMessage("");
            await refetch();
        } catch {
            setActionError(t("complaints.actionError"));
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) {
        return (
            <TaleDashbaordMainCard className="flex items-center justify-center min-h-[400px]">
                <TaleLoadingFallback />
            </TaleDashbaordMainCard>
        );
    }

    if (error || !complaint) {
        return (
            <TaleDashbaordMainCard className="flex items-center justify-center min-h-[400px]">
                <InternalServerError />
            </TaleDashbaordMainCard>
        );
    }

    const author = complaint.author || {};
    const company = complaint.company;

    return (
        <>
            {/* Resolve Modal */}
            {showResolveModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {t("complaints.resolveModalTitle")}
                            </h3>
                            <p className="text-gray-600 mb-4">{t("complaints.resolveModalText")}</p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("complaints.closingMessageLabel")}
                                </label>
                                <textarea
                                    value={closingMessage}
                                    onChange={(e) => setClosingMessage(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    rows={3}
                                    placeholder={t("complaints.closingMessagePlaceholder")}
                                />
                            </div>

                            <div className="flex justify-end gap-3">
                                <TaleButton
                                    variant="outlined"
                                    onClick={() => setShowResolveModal(false)}
                                    disabled={isProcessing}
                                >
                                    {t("common.cancel")}
                                </TaleButton>
                                <TaleButton
                                    onClick={confirmResolve}
                                    loading={isProcessing}
                                    className="bg-green-600 hover:bg-green-700 border-green-600"
                                >
                                    {t("complaints.confirmResolve")}
                                </TaleButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <TaleDashbaordMainCard className={"flex flex-col gap-6"}>
                {/* Header */}
                <div className="pb-4 border-b border-gray-200">
                    <button
                        onClick={() => navigate(basePath)}
                        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-3"
                    >
                        <FiArrowLeft className="w-4 h-4 rtl:-scale-x-100" />
                        {t("complaints.back")}
                    </button>

                    <div className="w-full flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                        <div className="flex flex-col gap-2">
                            <h2 className="!text-2xl font-bold !m-0">{complaint.subject}</h2>
                            <div className="flex flex-wrap items-center gap-2">
                                <ComplaintStatusBadge status={complaint.status} />
                                {complaint.category && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border bg-gray-50 text-gray-700 border-gray-200">
                        {t(`complaints.category.${complaint.category}`)}
                      </span>
                                )}
                                <span className="text-sm text-gray-500">#{complaint.id}</span>
                            </div>
                        </div>

                        {isAdmin && !isResolved && (
                            <TaleButton
                                className="bg-green-600 hover:bg-green-700 border-green-600"
                                onClick={() => setShowResolveModal(true)}
                                disabled={isProcessing}
                            >
                                <FiCheckCircle className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" />
                                {t("complaints.resolve")}
                            </TaleButton>
                        )}
                    </div>
                </div>

                {/* Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {isAdmin && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">{t("complaints.clientSection")}</h4>
                            <div className="flex items-center gap-3">
                                <Avatar person={author} />
                                <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{fullName(author)}</div>
                                    <div className="text-sm text-gray-500 truncate">{author.email}</div>
                                    {author.phoneNumber && (
                                        <div className="text-sm text-gray-500">{author.phoneNumber}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {isAdmin && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">{t("complaints.companySection")}</h4>
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 h-9 w-9 bg-white border rounded-lg flex items-center justify-center overflow-hidden">
                                    {company?.logoUrl ? (
                                        <img src={company.logoUrl} alt="" className="h-full w-full object-contain" />
                                    ) : (
                                        <FiBriefcase className="w-4 h-4 text-gray-500" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{company?.companyName || "-"}</div>
                                    {company?.address && (
                                        <div className="text-sm text-gray-500 truncate">{company.address}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                        <div>
                            <span className="text-gray-500">{t("complaints.createdAt")} : </span>
                            <span className="font-medium text-gray-900">{formatDateTime(complaint.createdAt)}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">{t("complaints.tableLastActivity")} : </span>
                            <span className="font-medium text-gray-900">{formatDateTime(complaint.lastMessageAt)}</span>
                        </div>
                        {isResolved && (
                            <div>
                                <span className="text-gray-500">{t("complaints.resolvedAt")} : </span>
                                <span className="font-medium text-gray-900">
                      {formatDateTime(complaint.resolvedAt)}
                                    {complaint.resolvedBy && ` (${fullName(complaint.resolvedBy)})`}
                    </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Thread */}
                <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-medium text-gray-700">
                        {t("complaints.thread")} ({messages.length})
                    </h4>

                    <div className="flex flex-col gap-4 bg-gray-50 border rounded-xl p-4 max-h-[55vh] overflow-y-auto">
                        {!messages.length && (
                            <p className="text-center text-gray-500 py-6">{t("complaints.noMessages")}</p>
                        )}

                        {messages.map((m) => {
                            // Admin messages on one side, client messages on the other
                            const mine = isAdmin ? m.fromAdmin : !m.fromAdmin;
                            return (
                                <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
                                    <Avatar person={m.sender} admin={m.fromAdmin} />
                                    <div className={`flex flex-col max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
                                        <div className="text-xs text-gray-500 mb-1">
                                            {m.fromAdmin ? t("complaints.supportLabel") : fullName(m.sender) || fullName(author)}
                                            {" · "}
                                            {formatDateTime(m.createdAt)}
                                        </div>
                                        <div
                                            className={`px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                                                mine
                                                    ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                                                    : "bg-white border text-gray-900"
                                            }`}
                                        >
                                            {m.text}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={threadEndRef} />
                    </div>
                </div>

                {/* Reply */}
                {isResolved ? (
                    <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">
                        <FiCheckCircle className="w-4 h-4" />
                        {t("complaints.resolvedBanner")}
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend();
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                    placeholder={t("complaints.replyPlaceholder")}
                    disabled={isProcessing}
                />
                        <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">{t("complaints.sendHint")}</span>
                            <TaleButton onClick={handleSend} loading={isProcessing} disabled={!reply.trim()}>
                                <FiSend className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 rtl:-scale-x-100" />
                                {t("complaints.send")}
                            </TaleButton>
                        </div>
                    </div>
                )}

                {actionError && <div className="text-sm text-red-600">{actionError}</div>}
            </TaleDashbaordMainCard>
        </>
    );
};

export default ComplaintDetails;